import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PratusTable from './PratusTable';
import {
  ColumnDef,
  HeaderContext,
  Row,
  SortingState,
  CellContext,
} from '@tanstack/react-table';
import {
  toColumnDefs,
  makeGetRowIdFromKeyField,
  LegacySortFn,
} from './legacyTableUtils';
import type { ColumnDescription } from 'react-bootstrap-table-next';
import ExpandDownArrow from '../buttons/ExpandDownArrow';
import ExpandRightArrow from '../buttons/ExpandRightArrow';

export type LegacyExpandRowKey = string | number;

export interface LegacyExpandRowConfig<RowT = any> {
  renderer: (row: RowT, rowIndex?: number) => React.ReactElement;
  showExpandColumn?: boolean;
  onlyOneExpanding?: boolean;
  nonExpandable?: LegacyExpandRowKey[];
  parentClassName?: string;
  className?: string;
  expanded?: string[];
  onExpand?: (row: RowT, isExpand: boolean, rowIndex: number) => void;
}

export interface LegacySelectRowConfig<RowT = any> {
  mode: 'checkbox' | 'radio';
  clickToSelect?: boolean;
  hideSelectAll?: boolean;
  selected?: LegacyExpandRowKey[];
  onSelect?: (
    row: RowT,
    isSelect: boolean,
    rowIndex: number,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSelectAll?: (
    isSelect: boolean,
    rows: RowT[],
    e?: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

export interface LegacyPratusTableProps {
  classes?: string;
  headerClasses?: string;
  columns: ColumnDescription[];
  rows: any[];
  keyField: string;
  noDataIndication?: string | React.ReactElement;
  expandRow?: LegacyExpandRowConfig;
  selectRow?: LegacySelectRowConfig;
  hasPagination?: boolean;
  paginationPerPageItem?: number | string;
  // External/server-side pagination (controlled)
  pagination?: boolean;
  page?: number;
  perPage?: number;
  total?: number;
  onPageChange?: (page: number) => void;
}

export default function LegacyPratusTable({
  classes,
  headerClasses,
  columns,
  rows,
  keyField,
  noDataIndication,
  expandRow,
  selectRow,
  hasPagination,
  paginationPerPageItem,
  pagination,
  page,
  perPage,
  total,
  onPageChange,
}: LegacyPratusTableProps) {
  const baseColumnDefs: ColumnDef<any, any>[] = toColumnDefs(columns);
  const getRowId = makeGetRowIdFromKeyField(keyField);
  const [internalPage, setInternalPage] = useState(1);

  const normalizedRows = rows ?? [];
  const hasInternalPagination = Boolean(hasPagination) && !pagination;
  const defaultInternalPerPage = useMemo(() => {
    const parsed = Number(paginationPerPageItem);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  }, [paginationPerPageItem]);
  const [internalPerPage, setInternalPerPage] = useState(
    defaultInternalPerPage
  );
  const internalTotal = normalizedRows.length;
  const internalPageCount = Math.max(
    1,
    Math.ceil(internalTotal / internalPerPage)
  );
  const internalPageIndex = Math.max(
    0,
    Math.min(internalPage - 1, internalPageCount - 1)
  );
  const pagedRows = hasInternalPagination
    ? normalizedRows.slice(
        internalPageIndex * internalPerPage,
        internalPageIndex * internalPerPage + internalPerPage
      )
    : normalizedRows;

  useEffect(() => {
    if (!hasInternalPagination) return;
    setInternalPage((prev) =>
      prev > internalPageCount ? internalPageCount : prev
    );
  }, [hasInternalPagination, internalPageCount]);

  useEffect(() => {
    setInternalPerPage(defaultInternalPerPage);
  }, [defaultInternalPerPage]);

  const nonExpandableSet = useMemo(() => {
    const keys = expandRow?.nonExpandable ?? [];
    return new Set(keys.map((k) => String(k)));
  }, [expandRow?.nonExpandable]);

  const hasExpand = Boolean(expandRow?.renderer);
  const showExpandColumn = Boolean(expandRow?.showExpandColumn);
  const hasSelect = Boolean(selectRow?.mode);
  const selectedRowSet = useMemo(() => {
    const keys = selectRow?.selected ?? [];
    return new Set(keys.map((k) => String(k)));
  }, [selectRow?.selected]);

  const getRowKey = useCallback(
    (row: Row<any>) => String(row.original?.[keyField]),
    [keyField]
  );

  const isRowExpandable = useCallback(
    (row: Row<any>) => !nonExpandableSet.has(getRowKey(row)),
    [getRowKey, nonExpandableSet]
  );

  const notifyExpandChange = useCallback(
    (row: Row<any>, isExpand: boolean) => {
      expandRow?.onExpand?.(row.original, isExpand, row.index);
    },
    [expandRow]
  );

  const getExpandableRows = useCallback(
    (rowsInTable: Row<any>[]) => rowsInTable.filter(isRowExpandable),
    [isRowExpandable]
  );

  const columnDefs = useMemo(() => {
    const selectCol: ColumnDef<any, any> | null = hasSelect
      ? {
          id: '__select',
          enableSorting: false,
          header: (ctx: HeaderContext<any, any>) => {
            if (selectRow?.hideSelectAll || selectRow?.mode === 'radio') {
              return null;
            }

            const rowsInTable = ctx.table.getRowModel().rows;
            const hasRows = rowsInTable.length > 0;
            const allSelected =
              hasRows &&
              rowsInTable.every((row) => selectedRowSet.has(getRowKey(row)));

            const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
              selectRow?.onSelectAll?.(
                e.target.checked,
                rowsInTable.map((row) => row.original),
                e
              );
            };

            return (
              <div className="form-checkbox align-items-center">
                <input
                  type={selectRow?.mode ?? 'checkbox'}
                  checked={allSelected}
                  disabled={!hasRows}
                  onChange={toggleAll}
                  name={'legacy-pratus-select'}
                />
                <span className="icon"></span>
              </div>
            );
          },
          cell: (ctx: CellContext<any, any>) => {
            const row = ctx.row;
            const rowKey = getRowKey(row);
            const isSelected = selectedRowSet.has(rowKey);

            const toggleRow = (e: React.ChangeEvent<HTMLInputElement>) => {
              selectRow?.onSelect?.(
                row.original,
                e.target.checked,
                row.index,
                e
              );
            };

            return (
              <div className="form-checkbox align-items-center">
                <input
                  type={selectRow?.mode ?? 'checkbox'}
                  checked={isSelected}
                  disabled={false}
                  onChange={toggleRow}
                  name={'legacy-pratus-select'}
                />
                <span className="icon"></span>
              </div>
            );
          },
          meta: {
            customSize: true,
          },
          size: 50,
        }
      : null;

    const expanderCol: ColumnDef<any, any> | null =
      hasExpand && showExpandColumn
        ? {
            id: '__expander',
            enableSorting: false,
            header: (ctx: HeaderContext<any, any>) => {
              const rowsInTable = ctx.table.getRowModel().rows;
              const expandableRows = getExpandableRows(rowsInTable);
              const hasExpandableRows = expandableRows.length > 0;
              const isAnyExpands = expandableRows.some((row) =>
                row.getIsExpanded()
              );

              const toggleAll = () => {
                if (!hasExpandableRows) return;

                const currentRows = getExpandableRows(
                  ctx.table.getRowModel().rows
                );

                if (expandRow?.onlyOneExpanding) {
                  // In single-expansion mode, opening from the header
                  // expands just the first expandable row.
                  const firstRow = currentRows[0];
                  if (!firstRow) return;
                  firstRow.toggleExpanded(true);
                  return;
                } else {
                  ctx.table.toggleAllRowsExpanded();
                }
              };

              return (
                <button
                  type="button"
                  onClick={toggleAll}
                  disabled={!hasExpandableRows}
                  aria-label={
                    isAnyExpands ? 'Collapse all rows' : 'Expand all rows'
                  }
                  title={isAnyExpands ? 'Collapse all rows' : 'Expand all rows'}
                  className="border-0 bg-transparent p-0"
                >
                  {isAnyExpands ? <ExpandDownArrow /> : <ExpandRightArrow />}
                </button>
              );
            },
            cell: (ctx: CellContext<any, any>) => {
              const row = ctx.row;
              const rowKey = getRowKey(row);
              const expandable = isRowExpandable(row);
              const isExpanded = row.getIsExpanded();

              const toggleExpanded = () => {
                if (!expandable) return;
                row.toggleExpanded();
                notifyExpandChange(row, !isExpanded);
              };

              const content = !expandable ? null : (
                <>
                  <span aria-hidden="true">
                    {isExpanded ? <ExpandDownArrow /> : <ExpandRightArrow />}
                  </span>
                  <span className="visually-hidden">
                    {isExpanded ? 'Collapse row' : 'Expand row'}
                  </span>
                </>
              );

              // Expander-column-only: the only toggle handler is on this button.
              // (We purposely do not wire row-click expansion.)
              return (
                <button
                  type="button"
                  data-testid={`legacy-pratus-expander-${rowKey}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleExpanded();
                  }}
                  disabled={!expandable}
                  aria-expanded={isExpanded}
                  aria-disabled={!expandable}
                  title={
                    !expandable
                      ? 'Row is not expandable'
                      : isExpanded
                        ? 'Collapse row'
                        : 'Expand row'
                  }
                  className="border-0 bg-transparent p-0"
                >
                  {content}
                </button>
              );
            },
            meta: {
              customSize: true,
            },
            size: 50,
          }
        : null;

    if (!selectCol && !expanderCol) return baseColumnDefs;

    return [
      ...(selectCol ? [selectCol] : []),
      ...(expanderCol ? [expanderCol] : []),
      ...baseColumnDefs,
    ];
  }, [
    baseColumnDefs,
    getExpandableRows,
    getRowKey,
    expandRow,
    hasExpand,
    hasSelect,
    isRowExpandable,
    notifyExpandChange,
    selectRow,
    selectedRowSet,
    showExpandColumn,
  ]);

  const hasLegacyOnSort = columnDefs.some((c) => {
    const meta = c.meta as any;
    return typeof meta?.legacyOnSort === 'function';
  });

  const handleSortChange = (nextState: SortingState) => {
    if (!hasLegacyOnSort) return;

    // Legacy Sorts only support a single sort.
    const next = nextState[0];

    if (!next) {
      // clearing sort
      // we need to find any onSort and call that with undefined, undefined.
      // all onSorts will "unset" the sortBy if called with undefined, undefined.
      const anySortedCol = columnDefs.find((c) => {
        const meta = c.meta as any;
        if (meta.legacyOnSort) {
          return true;
        }
      });
      if (anySortedCol) {
        const meta = anySortedCol.meta as any;
        const onSort = meta.legacyOnSort as LegacySortFn | undefined;
        if (onSort) {
          onSort(undefined, undefined);
        }
      }
    } else {
      const sortedCol = columnDefs.find((c) => c.id === next.id);
      const meta = (sortedCol?.meta as any) ?? {};
      const onSort = meta.legacyOnSort as LegacySortFn | undefined;
      if (onSort) {
        onSort(undefined, next.desc ? 'desc' : 'asc');
      }
    }
  };

  const tableEl = (
    <PratusTable
      className={classes}
      headerClassName={headerClasses}
      columns={columnDefs}
      data={pagedRows}
      getRowId={getRowId}
      noDataIndication={noDataIndication}
      manualSorting={hasLegacyOnSort}
      onSortingChange={handleSortChange}
      expansion={
        hasExpand
          ? {
              getRowCanExpand: isRowExpandable,
              singleExpansion: Boolean(expandRow?.onlyOneExpanding),
              expandedRowModel: (row: any) => {
                return (
                  <div className="row-expansion-style">
                    {expandRow!.renderer(row)}
                  </div>
                );
              },
              getExpandedRowClassName: () => expandRow?.className,
            }
          : undefined
      }
      getRowClassName={
        hasExpand
          ? (row: Row<any>) =>
              row.getIsExpanded() ? expandRow?.parentClassName : undefined
          : undefined
      }
      pagination={(() => {
        if (hasInternalPagination) {
          return {
            page: internalPage,
            pageCount: internalPageCount,
            onPageChange: setInternalPage,
            perPage: internalPerPage,
            onPerPageChange: (nextPerPage: number) => {
              setInternalPerPage(nextPerPage);
              setInternalPage(1);
            },
          };
        }

        if (!pagination) return undefined;

        const validPerPage = perPage && perPage > 0 ? perPage : 10;
        const validTotal = total ?? normalizedRows.length ?? 0;
        const pageCount = Math.max(1, Math.ceil(validTotal / validPerPage));
        const currentPage = Math.max(1, Math.min(page ?? 1, pageCount));

        return {
          page: currentPage,
          pageCount,
          onPageChange: (nextPage: number) => onPageChange?.(nextPage),
          perPage: validPerPage,
        };
      })()}
    />
  );

  return tableEl;
}
